import { voteTargetKey, type CommentView, type RetroSnapshot } from '@retro/shared';

export type BucketEntry = {
  comment: CommentView;
  votes: number;
  columnTitle: string;
  groupTitle: string | null;
};

export type TopicBucket = {
  id: string;
  name: string;
  color: string;
  votes: number;
  entries: BucketEntry[];
};

export const UNTAGGED_BUCKET_ID = '__none__';

/** Votes attach to a votable unit: a group if the comment is in one, else itself. */
export function voteUnitKey(comment: Pick<CommentView, 'id' | 'groupId'>): string {
  return comment.groupId
    ? voteTargetKey({ type: 'GROUP', id: comment.groupId })
    : voteTargetKey({ type: 'COMMENT', id: comment.id });
}

/**
 * Regroup a closed retro's comments by topic for the report.
 *
 * After the fact the useful question is "which themes keep coming up", not
 * "which box did this start in", so the report is organised by topic rather than
 * by column.
 *
 * The subtlety is the per-topic vote total: votes belong to a *unit* (a group or
 * a lone comment), so each unit must be counted once per bucket. Summing
 * per-comment votes would multiply a group's total by however many of its cards
 * carry that topic.
 */
export function buildTopicBuckets(snapshot: RetroSnapshot): TopicBucket[] {
  const columnTitle = (columnId: string) =>
    snapshot.columns.find((column) => column.id === columnId)?.title ?? '';

  const groupTitle = (groupId: string | null) =>
    groupId ? (snapshot.groups.find((group) => group.id === groupId)?.title || 'Grouped comments') : null;

  const votesFor = (comment: CommentView) => snapshot.votes.totals[voteUnitKey(comment)] ?? 0;

  const buckets = new Map<string, TopicBucket>();
  const untagged: TopicBucket = {
    id: UNTAGGED_BUCKET_ID,
    name: 'No topic',
    color: '#94a3b8',
    votes: 0,
    entries: [],
  };

  const countedUnits = new Map<string, Set<string>>();
  const addVotes = (bucket: TopicBucket, comment: CommentView, votes: number) => {
    const seen = countedUnits.get(bucket.id) ?? new Set<string>();
    const key = voteUnitKey(comment);
    if (!seen.has(key)) {
      seen.add(key);
      bucket.votes += votes;
    }
    countedUnits.set(bucket.id, seen);
  };

  for (const comment of snapshot.comments) {
    const entry: BucketEntry = {
      comment,
      votes: votesFor(comment),
      columnTitle: columnTitle(comment.columnId),
      groupTitle: groupTitle(comment.groupId),
    };

    if (comment.topicIds.length === 0) {
      untagged.entries.push(entry);
      addVotes(untagged, comment, entry.votes);
      continue;
    }

    for (const topicId of comment.topicIds) {
      const topic = snapshot.topics.find((candidate) => candidate.id === topicId);
      if (!topic) continue;

      const bucket =
        buckets.get(topicId) ?? { id: topicId, name: topic.name, color: topic.color, votes: 0, entries: [] };
      bucket.entries.push(entry);
      addVotes(bucket, comment, entry.votes);
      buckets.set(topicId, bucket);
    }
  }

  const sorted = [...buckets.values()].sort((a, b) => b.votes - a.votes || a.name.localeCompare(b.name));
  if (untagged.entries.length > 0) sorted.push(untagged);

  for (const bucket of sorted) bucket.entries.sort((a, b) => b.votes - a.votes);
  return sorted;
}
